import { ChevronLeft } from "lucide-react";

const PrivacyPage = ({ navigateTo }) => {
  return (
    <div className="min-h-screen py-16 px-4" style={{ background: "var(--zen-bg)" }}>
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigateTo("landing")}
          className="flex items-center gap-1 text-sm mb-8 hover:opacity-70 transition-opacity"
          style={{ color: "var(--zen-sub)" }}
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="font-heading text-3xl sm:text-4xl mb-2" style={{ color: "var(--zen-text)" }}>
          Privacy Policy
        </h1>
        <p className="text-sm mb-10" style={{ color: "var(--zen-dim)" }}>
          Last updated: March 19, 2026
        </p>

        <div className="space-y-8 text-sm leading-relaxed" style={{ color: "var(--zen-sub)" }}>
          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>1. Introduction</h2>
            <p>
              JapanUnfolded ("we", "us", "our") respects your privacy. This Privacy Policy explains what
              information we collect, how we use it, and your rights regarding your data when you use
              our service at japanunfolded.ai.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>2. Information We Collect</h2>
            <p className="mb-3"><strong style={{ color: "var(--zen-text)" }}>Information you provide:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mb-4">
              <li>Email address (for authentication and purchase receipts)</li>
              <li>Travel preferences (dates, cities, interests, pace)</li>
              <li>Text input describing desired places or experiences</li>
              <li>Saved itineraries and trip configurations</li>
            </ul>
            <p className="mb-3"><strong style={{ color: "var(--zen-text)" }}>Information collected automatically:</strong></p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Browser type and device information</li>
              <li>Pages visited and features used</li>
              <li>IP address (for security and fraud prevention)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>3. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>To generate personalized travel itineraries using AI</li>
              <li>To process payments and manage your credit balance</li>
              <li>To send magic link authentication emails</li>
              <li>To save and retrieve your trip data</li>
              <li>To improve our service and fix issues</li>
              <li>To prevent fraud and abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>4. Third-Party Services</h2>
            <p className="mb-3">We use the following third-party services to operate JapanUnfolded:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong style={{ color: "var(--zen-text)" }}>Stripe</strong> — Payment processing.
                Stripe collects and processes your payment information directly. We never see or store
                your full card number. See{" "}
                <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70" style={{ color: "var(--zen-accent)" }}>
                  Stripe's Privacy Policy
                </a>.
              </li>
              <li>
                <strong style={{ color: "var(--zen-text)" }}>Anthropic (Claude) API</strong> — AI-powered
                itinerary generation. Your travel preferences, text input and any screenshot or link you add are sent
                to this AI provider to generate recommendations. See{" "}
                <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70" style={{ color: "var(--zen-accent)" }}>
                  Anthropic's Privacy Policy
                </a>.
              </li>
              <li>
                <strong style={{ color: "var(--zen-text)" }}>Google Maps Platform (Places)</strong> — to confirm that a
                place you ask for exists, is open and is well reviewed, its name is looked up on Google. See{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-70" style={{ color: "var(--zen-accent)" }}>
                  Google's Privacy Policy
                </a>.
              </li>
              <li>
                <strong style={{ color: "var(--zen-text)" }}>MongoDB Atlas</strong> — Database hosting
                for storing user accounts, itineraries, and credit balances.
              </li>
              <li>
                <strong style={{ color: "var(--zen-text)" }}>Resend</strong> — Email delivery for
                magic link authentication.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>5. Data Storage and Security</h2>
            <p>
              Your data is stored securely in MongoDB Atlas with encryption at rest and in transit.
              Authentication uses time-limited magic link tokens (30-day expiry). We do not store
              passwords. We implement reasonable security measures to protect your information, but no
              method of transmission over the internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>6. Data Retention</h2>
            <p>
              We retain your account data and saved itineraries for as long as your account is active.
              Authentication sessions expire after 30 days. You may request deletion of your data at any
              time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>7. Your Rights</h2>
            <p className="mb-2">You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent for data processing</li>
              <li>Request a copy of your data in a portable format</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, contact us at{" "}
              <a href="mailto:support@japanunfolded.ai" className="underline hover:opacity-70" style={{ color: "var(--zen-accent)" }}>
                support@japanunfolded.ai
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>8. Cookies and Local Storage</h2>
            <p>
              JapanUnfolded uses browser localStorage to store your session token and email for
              authentication purposes. We do not use tracking cookies or third-party advertising cookies.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>9. Children's Privacy</h2>
            <p>
              JapanUnfolded is not directed to children under 13. We do not knowingly collect personal
              information from children under 13. If you believe a child has provided us with personal
              information, please contact us.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page
              with an updated date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--zen-text)" }}>11. Contact</h2>
            <p>
              For privacy-related questions or requests, please contact us at{" "}
              <a href="mailto:support@japanunfolded.ai" className="underline hover:opacity-70" style={{ color: "var(--zen-accent)" }}>
                support@japanunfolded.ai
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
